import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Spinner, Text, TextField } from "@merge-api/merge-javascript-shared";
import { Search } from "lucide-react";
import { showErrorToast, showSuccessToast } from "../../shared/Toasts";
import { conductorPost } from "../conductorApi";
import { useConductorResource } from "../hooks/useConductorResource";
import { TenantSchedulingConfig } from "./types";

// Max concurrent syncs per third-party tenant. Note this tenant_id is the
// identifier from the third-party provider, not a Merge deployment like the
// tenants dimension on a flag rule.
const TenantLimitsPage = () => {
  // hooks
  const location = useLocation();

  // derived state
  // Account speeds links here with a tenant id, since there is no list-all RPC
  // to browse and that table is the only discovery path.
  const linkedTenantId = new URLSearchParams(location.search).get("tenant_id") || "";

  // state
  const [tenantInput, setTenantInput] = useState(linkedTenantId);
  const [tenantId, setTenantId] = useState(linkedTenantId);
  const [reloadToken, setReloadToken] = useState(0);
  const [concurrency, setConcurrency] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // hooks
  const { data: config, isLoading } = useConductorResource<TenantSchedulingConfig>(
    tenantId
      ? `/conductor/tenant-scheduling-configs?tenant_id=${encodeURIComponent(tenantId)}`
      : null,
    undefined,
    reloadToken
  );

  // derived state
  const parsed = Number(concurrency);
  const isValid = Number.isInteger(parsed) && parsed >= 1;
  const isDirty = !config || parsed !== config.max_concurrency;

  // Seed the input from whatever the lookup returned; an unknown tenant has no
  // config yet and starts blank so saving creates one.
  useEffect(() => {
    setConcurrency(config ? String(config.max_concurrency) : "");
  }, [config]);

  // event handlers
  const save = async () => {
    setIsSaving(true);
    const { ok, error } = await conductorPost("/conductor/tenant-scheduling-configs", {
      tenant_id: tenantId,
      max_concurrency: parsed,
    });
    setIsSaving(false);
    if (ok) {
      showSuccessToast("Tenant limit saved");
      setReloadToken((token) => token + 1);
    } else {
      showErrorToast(error || "Failed to save tenant limit");
    }
  };

  // render
  return (
    <>
      <div className="mb-6">
        <Text variant="h5">Tenant limits</Text>
        <Text variant="sm" className="mt-1 block text-gray-60">
          Maximum concurrent syncs for a third-party provider tenant. This is the provider's own
          tenant identifier, not a Merge deployment.
        </Text>
      </div>

      <div className="mb-6 flex items-end gap-2">
        <div className="w-96">
          <Text variant="h6" className="mb-2">
            Tenant ID
          </Text>
          <TextField
            value={tenantInput}
            onChange={(event) => setTenantInput(event.target.value)}
            placeholder="the provider's tenant identifier"
          />
        </div>
        <Button
          size="md"
          leftIcon={<Search size={16} />}
          disabled={tenantInput.trim() === ""}
          onClick={() => setTenantId(tenantInput.trim())}
        >
          Look up
        </Button>
      </div>

      {!tenantId ? (
        <Text variant="md" className="block py-16 text-center text-gray-60">
          Enter a tenant ID to see or set its concurrency limit.
        </Text>
      ) : isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <div className="max-w-xl rounded-lg border border-gray-10 bg-white p-4">
          <div className="mb-4 flex items-baseline gap-2">
            <Text variant="h6" className="font-mono">
              {tenantId}
            </Text>
            <Text variant="sm" className="text-gray-40">
              {config ? "existing limit" : "no limit set yet"}
            </Text>
          </div>
          <div className="w-40">
            <TextField
              labelTitle="Max concurrency"
              labelSubtitle="Must be 1 or more"
              type="number"
              value={concurrency}
              onChange={(event) => setConcurrency(event.target.value)}
              error={concurrency !== "" && !isValid}
              errorText={
                concurrency !== "" && !isValid ? "Must be a whole number, 1 or more" : undefined
              }
            />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button
              size="sm"
              onClick={save}
              disabled={!isValid || !isDirty || isSaving}
              loading={isSaving}
            >
              {config ? "Save limit" : "Create limit"}
            </Button>
            {config?.modified_at && (
              <Text variant="sm" className="text-gray-40">
                Last changed {config.modified_at}
              </Text>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default TenantLimitsPage;
